import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../hooks/UserContext';
import { apiClient } from '../apiClient';
import { auth } from '../firebase';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ja } from 'date-fns/locale';
import "react-datepicker/dist/react-datepicker.css";
import { X, ChevronRight } from 'lucide-react';

registerLocale('ja', ja);

// --- 定数定義 (全件検索用 & 翻訳キー同期) ---
const COMMON_ACTIONS = [
    'member_login_success', 
    'invoice_csv_upload', 
    'invoice_deleted', 
    'customer_csv_upload', 
    'customer_delete', 
    'member_invited', 
    'member_removed', 
    'plan_changed',
    'account_canceled',
    'account_reactivated',
    'stripe_api_key_updated',
    'tenant_settings_updated',
    'tax_rate_created',
    'tax_rate_updated',
    'tax_rate_deleted'
];

const formatLabel = (key, t) => {
    const permKey = `permission_${key}`;
    const translated = t(permKey);
    if (translated && translated !== permKey) return translated;
    return t(key, key);
};

const formatVal = (val, t) => {
    if (val === true) return <span className="text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">{t('enabled', '有効')}</span>;
    if (val === false) return <span className="text-gray-500 font-bold bg-gray-100 px-2 py-0.5 rounded border border-gray-200">{t('disabled', '無効')}</span>;
    if (val === null || val === undefined) return <span className="text-gray-400">-</span>;
    return String(val);
};

const DetailItem = ({ label, value }) => (
  <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 whitespace-pre-wrap break-words">{value}</dd>
  </div>
);

const DetailsObject = ({ data, t }) => {
    if (!data || typeof data !== 'object') return <>{String(data)}</>;
    if (Array.isArray(data.error)) {
        return (
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse border border-gray-300" style={{tableLayout: 'fixed'}}>
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="border p-2 font-semibold w-16 text-left">行</th>
                            <th className="border p-2 font-semibold w-24 text-left">コード</th>
                            <th className="border p-2 font-semibold text-left">内容</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.error.map((errorItem, index) => (
                            <tr key={index}>
                                <td className="border p-2 text-center">{errorItem.row || '-'}</td>
                                <td className="border p-2 break-words text-red-600">{errorItem.code || '-'}</td>
                                <td className="border p-2 break-words">{errorItem.message}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    // メンバー権限更新 (from, to がオブジェクトとして含まれている場合) の差分テーブル表示
    if (data.from && typeof data.from === 'object' && data.to && typeof data.to === 'object') {
        const categories = Array.from(new Set([...Object.keys(data.from || {}), ...Object.keys(data.to || {})]));
        return (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                <table className="w-full text-xs border-collapse">
                    <thead className="bg-gray-100 text-gray-700 font-bold border-b border-gray-200">
                        <tr>
                            <th className="p-2.5 text-left w-2/5">{t('permissionItem', '権限項目')}</th>
                            <th className="p-2.5 text-left w-[30%] border-l border-gray-200 bg-red-50/50 text-red-900">{t('beforeChange', '変更前')}</th>
                            <th className="p-2.5 text-left w-[30%] border-l border-gray-200 bg-emerald-50/50 text-emerald-900">{t('afterChange', '変更後')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {categories.map((catKey) => {
                            const fromCat = data.from?.[catKey] || {};
                            const toCat = data.to?.[catKey] || {};
                            const permKeys = Array.from(new Set([...Object.keys(fromCat), ...Object.keys(toCat)]));
                            return (
                                <React.Fragment key={catKey}>
                                    <tr className="bg-gray-50/80 font-bold text-gray-800">
                                        <td colSpan="3" className="p-2">
                                            {formatLabel(catKey, t)}
                                        </td>
                                    </tr>
                                    {permKeys.map((pKey) => {
                                        const fromVal = fromCat[pKey];
                                        const toVal = toCat[pKey];
                                        const isDiff = fromVal !== toVal;
                                        return (
                                            <tr key={pKey} className={isDiff ? 'bg-amber-50/50' : 'hover:bg-gray-50/30'}>
                                                <td className="p-2 pl-4 text-gray-700 font-medium">
                                                    {formatLabel(pKey, t)}
                                                </td>
                                                <td className="p-2 border-l border-gray-100">
                                                    {formatVal(fromVal, t)}
                                                </td>
                                                <td className="p-2 border-l border-gray-100">
                                                    {formatVal(toVal, t)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    const renderValue = (value) => {
        if (typeof value === 'object' && value !== null) return <div className="pl-4 mt-1 border-l-2 border-gray-100"><DetailsObject data={value} t={t} /></div>;
        return <>{String(value)}</>;
    };

    return (
        <dl className="space-y-1">
            {Object.entries(data).map(([key, value]) => (
                <div key={key} className="py-2 sm:grid sm:grid-cols-3 sm:gap-4 border-b border-gray-50 last:border-0">
                    <dt className="text-xs font-bold text-gray-400 uppercase tracking-tight">{t(key, key)}</dt>
                    <dd className="text-sm text-gray-900 sm:col-span-2">{renderValue(value)}</dd>
                </div>
            ))}
        </dl>
    );
};

const renderResultBadge = (result, t) => {
    const isSuccess = result === 'success';
    const label = isSuccess ? t('success') : t('failed');
    return (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${isSuccess ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {label}
        </span>
    );
};

const LogDetailModal = ({ log, onClose, t }) => {
  if (!log) return null;
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl transform transition-all border border-gray-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
          <h2 className="text-lg font-bold text-gray-900">{t('logDetailTitle', 'ログ詳細')}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-full text-gray-400 hover:text-gray-600 transition-colors"><X className="h-6 w-6" /></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
          <dl className="divide-y divide-gray-200 border-t border-b border-gray-200">
            <DetailItem label={t('tableHeaderDate')} value={log.created_at.toLocaleString('ja-JP')} />
            <DetailItem label={t('tableHeaderActor')} value={log.actor_name || 'システム'} />
            <DetailItem label={t('tableHeaderAction')} value={<span className="font-bold text-indigo-600">{t(log.action, log.action)}</span>} />
            <DetailItem label={t('tableHeaderTarget')} value={log.target_name ? `${log.target_name} (ID: ${log.target_id || 'N/A'})` : (log.target_id || 'N/A')} />
            <DetailItem label={t('tableHeaderResult')} value={renderResultBadge(log.result, t)} />
             <div className="py-3">
                <dt className="text-sm font-medium text-gray-500 mb-2">詳細データ</dt>
                <dd className="mt-1 text-sm text-gray-900 bg-gray-50 p-4 rounded-lg border border-gray-100 shadow-inner"><DetailsObject data={log.details} t={t} /></dd>
            </div>
          </dl>
        </div>
        <div className="flex items-center justify-end p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-6 py-2 bg-gray-800 text-white rounded-lg shadow-md hover:bg-black font-bold transition-all text-sm">{t('closeButton', '閉じる')}</button>
        </div>
      </div>
    </div>
  );
};

function ActivityPage() {
  const { t } = useTranslation('activity');
  const { isAdmin, loading: authLoading } = useUser();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [lastVisibleId, setLastVisibleId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const initialStartDate = useMemo(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); d.setHours(0, 0, 0, 0); return d; }, []);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(new Date());
  const [actorSearch, setActorSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const PAGE_SIZE = 50;

  const fetchLogs = useCallback(async (cursor, filters) => {
    if (!isAdmin) { setLoading(false); return; }
    cursor === null ? setLoading(true) : setLoadingMore(true);
    setError('');
    try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error('User not authenticated');
        const token = await currentUser.getIdToken();
        const params = { ...filters };
        if (cursor) params.lastDocId = cursor;
        const queryString = new URLSearchParams(params).toString();
        const response = await apiClient.get(`/v1/activities/search?${queryString}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.error || t('errorLoadingLogs')); }
        const logsFromServer = await response.json();
        setHasMore(logsFromServer.length === PAGE_SIZE);
        setLastVisibleId(logsFromServer.length > 0 ? logsFromServer[logsFromServer.length - 1].id : null);
        let finalLogs = logsFromServer.map(log => ({ ...log, created_at: log.created_at ? new Date(log.created_at._seconds * 1000) : new Date() }));
        if (filters.actorSearch) { const searchLower = filters.actorSearch.toLowerCase(); finalLogs = finalLogs.filter(log => (log.actor_name || '').toLowerCase().includes(searchLower)); }
        setLogs(prev => cursor ? [...prev, ...finalLogs] : finalLogs);
    } catch (err) { console.error("Error fetching activity logs:", err); setError(err.message || t('errorLoadingLogs')); } finally { setLoading(false); setLoadingMore(false); }
  }, [isAdmin, t]);

  const handleSearch = () => {
      const filters = { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
      if (actionFilter) filters.action = actionFilter;
      if (resultFilter) filters.result = resultFilter;
      if (actorSearch) filters.actorSearch = actorSearch;
      setActiveFilters(filters); setLogs([]); setLastVisibleId(null); fetchLogs(null, filters);
  };

  const handleReset = () => {
      setStartDate(initialStartDate); setEndDate(new Date()); setActorSearch(''); setActionFilter(''); setResultFilter('');
      const emptyFilters = { startDate: initialStartDate.toISOString(), endDate: new Date().toISOString() };
      setActiveFilters(emptyFilters); setLogs([]); setLastVisibleId(null); fetchLogs(null, emptyFilters);
  };

  const handleLoadMore = () => { if (lastVisibleId && hasMore && !loading && !loadingMore) fetchLogs(lastVisibleId, activeFilters); };

  useEffect(() => { if (!authLoading && isAdmin) { const initialFilters = { startDate: initialStartDate.toISOString(), endDate: new Date().toISOString() }; setActiveFilters(initialFilters); fetchLogs(null, initialFilters); } }, [authLoading, isAdmin, initialStartDate, fetchLogs]);
  
  const actionOptions = useMemo(() => ['', ...COMMON_ACTIONS].sort(), []);

  if (authLoading) return <div className="p-8 text-center">{t('loadingUserData')}</div>;
  if (!isAdmin) return <div className="p-8 text-center text-red-500">{t('noPermission')}</div>;
  
  return (
    <div className="bg-white p-8 w-full max-w-6xl mx-auto my-8 shadow-xl rounded-2xl border border-gray-100 text-sans">
        <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-100">
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">{t('mainTitle')}</h1>
        </div>
        <div className="bg-gray-50 p-6 rounded-xl mb-8 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-2">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">{t('dateRangeLabel')}</label>
              <div className="flex items-center space-x-2">
                <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} showTimeSelect timeFormat="HH:mm" dateFormat="yyyy/MM/dd HH:mm" locale="ja" className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
                <span className="text-gray-400">〜</span>
                <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} showTimeSelect timeFormat="HH:mm" dateFormat="yyyy/MM/dd HH:mm" locale="ja" className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
            </div>
            <div>
              <label htmlFor="actorSearch" className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">{t('tableHeaderActor')}</label>
              <input type="text" id="actorSearch" value={actorSearch} onChange={(e) => setActorSearch(e.target.value)} placeholder="名前で検索" className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>
            <div>
              <label htmlFor="actionFilter" className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">{t('actionLabel')}</label>
              <select id="actionFilter" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="w-full p-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 outline-none">
                <option value="">すべてのアクション</option>
                {actionOptions.filter(Boolean).map(action => (<option key={action} value={action}>{t(action, action)}</option>))}
              </select>
            </div>
             <div>
              <label htmlFor="resultFilter" className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">{t('resultLabel')}</label>
              <select id="resultFilter" value={resultFilter} onChange={(e) => setResultFilter(e.target.value)} className="w-full p-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 outline-none">
                <option value="">すべての結果</option>
                <option value="success">{t('success')}</option>
                <option value="failed">{t('failed')}</option>
              </select>
            </div>
          </div>
           <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button onClick={handleSearch} disabled={loading || loadingMore} className="bg-indigo-600 text-white font-bold py-2.5 px-8 rounded-lg hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 disabled:bg-indigo-300">検索</button>
                <button onClick={handleReset} disabled={loading || loadingMore} className="bg-gray-100 text-gray-600 font-bold py-2.5 px-8 rounded-lg hover:bg-gray-200 transition-all">リセット</button>
            </div>
        </div>
        <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full border-collapse table-auto">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="p-4 text-left whitespace-nowrap text-xs font-bold text-gray-400 uppercase tracking-widest">{t('tableHeaderDate')}</th>
                  <th className="p-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">{t('tableHeaderActor')}</th>
                  <th className="p-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">{t('tableHeaderAction')}</th>
                  <th className="p-4 text-left whitespace-nowrap text-xs font-bold text-gray-400 uppercase tracking-widest">{t('tableHeaderResult')}</th>
                  <th className="p-4 text-left"><span className="sr-only">Details</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading && logs.length === 0 ? (
                  <tr><td colSpan="5" className="p-12 text-center text-gray-400 animate-pulse">{t('loadingLogs')}</td></tr>
                ) : error ? (
                  <tr><td colSpan="5" className="p-12 text-center text-red-500 font-medium">{error}</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan="5" className="p-12 text-center text-gray-400 italic">{t('noLogsFound')}</td></tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-indigo-50/30 transition-colors group">
                      <td className="p-4 whitespace-nowrap text-sm text-gray-600 font-medium">{log.created_at.toLocaleString('ja-JP')}</td>
                      <td className="p-4 text-sm text-gray-900 break-all font-bold">{log.actor_name || 'システム'}</td>
                      <td className="p-4 text-sm text-gray-700 break-all font-medium">{t(log.action, log.action)}</td>
                      <td className="p-4 whitespace-nowrap">
                          {renderResultBadge(log.result, t)}
                      </td>
                      <td className="p-4 text-center">
                        <button onClick={() => setSelectedLog(log)} className="text-indigo-400 group-hover:text-indigo-600 transition-all p-1.5 rounded-full hover:bg-white hover:shadow-sm"><ChevronRight size={20}/></button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
        </div>
          {hasMore && (
               <div className="flex justify-center mt-10">
                  <button onClick={handleLoadMore} disabled={loading || loadingMore} className="bg-white border border-gray-200 text-gray-700 font-bold py-2.5 px-12 rounded-xl hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all shadow-sm disabled:opacity-50">
                      {loadingMore ? t('loading') : 'さらに読み込む'}
                  </button>
              </div>
          )}
        {selectedLog && <LogDetailModal onClose={() => setSelectedLog(null)} log={selectedLog} t={t} />}
    </div>
  );
}

export default ActivityPage;
